import { com } from '@eidu/web'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import postEntity from './api/entity/data/postEntity'
import postEntities from './api/entity/data/postEntities'
import patchEntity from './api/entity/data/patchEntity'
import patchEntities from './api/entity/data/patchEntities'
import deleteEntity from './api/entity/data/deleteEntity'
import Patch, { hasModifications } from './api/entity/data/Patch'
import { auditLogKeys, entityKeys } from './queryKeys'
import EntityId = com.eidu.entity.EntityId
import EntityToCreate = com.eidu.entity.api.entities.EntityToCreate

export const useCreateEntity = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (entity: EntityToCreate): Promise<EntityId> => postEntity({ entity }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: entityKeys.all })
      queryClient.invalidateQueries({ queryKey: auditLogKeys.all })
    },
  })
}

export const useCreateEntities = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (entities: readonly EntityToCreate[]): Promise<readonly EntityId[]> => {
      const ids = await postEntities({ entities })
      return ids.ids.asJsReadonlyArrayView()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: entityKeys.all })
      queryClient.invalidateQueries({ queryKey: auditLogKeys.all })
    },
  })
}

export const useModifyEntity = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, patch }: { id: EntityId; patch: Patch }): Promise<EntityId> => {
      if (hasModifications(patch)) await patchEntity({ id, patch })
      return id
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: entityKeys.all })
      queryClient.invalidateQueries({ queryKey: auditLogKeys.all })
    },
  })
}

export const useModifyEntities = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (patchesByEntityId: Map<EntityId, Patch>): Promise<readonly EntityId[]> => {
      const patchesToSend = new Map([...patchesByEntityId].filter(([, patch]) => hasModifications(patch)))
      if (patchesToSend.size > 0) await patchEntities({ patchesByEntityId: patchesToSend })
      return Array.from(patchesByEntityId.keys())
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: entityKeys.all })
      queryClient.invalidateQueries({ queryKey: auditLogKeys.all })
    },
  })
}

export const useDeleteEntity = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: EntityId): Promise<EntityId> => {
      await deleteEntity({ id })
      return id
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: entityKeys.all })
      queryClient.invalidateQueries({ queryKey: auditLogKeys.all })
    },
  })
}
